import DOMPurify from 'dompurify'

/** Extract plain text from HTML string. Uses DOMParser to avoid triggering resource loading. */
export function htmlToPlain(html) {
    if (!html) return ''
    // Insert newlines before block elements so textContent preserves structure
    const withBreaks = html
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n')
        .replace(/<\/li>/gi, '\n')
        .replace(/<\/h[1-6]>/gi, '\n')
        .replace(/<\/pre>/gi, '\n')
        .replace(/<\/blockquote>/gi, '\n')
        .replace(/<hr\s*\/?>/gi, '\n---\n')
    const doc = new DOMParser().parseFromString(withBreaks, 'text/html')
    // Collapse multiple blank lines
    return (doc.body.textContent || '').replace(/\n{3,}/g, '\n\n').trim()
}

/** Sanitize HTML before setting it into the editor. */
export function sanitize(html) {
    return DOMPurify.sanitize(html, {
        ALLOWED_TAGS: [
            'p',
            'br',
            'b',
            'strong',
            'i',
            'em',
            'u',
            'h1',
            'h2',
            'h3',
            'ul',
            'ol',
            'li',
            'hr',
            'blockquote',
            'pre',
            'span',
        ],
        ALLOWED_ATTR: ['class'],
    })
}

/**
 * Convert plain text to basic HTML for the rich editor.
 * Escapes angle brackets so tags like <MainConcepts> render as visible text.
 */
export function plainTextToHtml(text) {
    if (!text) return ''
    const html = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .split('\n')
        .map(line => (line.trim() === '' ? '<br>' : `<p>${line}</p>`))
        .join('')
    return sanitize(html)
}

/**
 * Convert markdown-style text to HTML.
 * Supports: **bold**, *italic*, headings (#, ##, ###),
 * unordered lists (- item), numbered lists (1. item), and paragraphs.
 */
export function markdownToHtml(text) {
    if (!text) return ''

    const lines = text.split('\n')
    const result = []
    let inUl = false
    let inOl = false
    let inOlSub = false // sub-list (- items) inside a numbered list

    const closeSubList = () => {
        if (inOlSub) {
            result.push('</ul>')
            inOlSub = false
        }
    }

    const closeAllLists = () => {
        closeSubList()
        if (inUl) {
            result.push('</ul>')
            inUl = false
        }
        if (inOl) {
            result.push('</ol>')
            inOl = false
        }
    }

    const formatInline = line => {
        return line
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/\*\*(.+?)\*\*/g, '<b>$1</b>')
            .replace(/\*(.+?)\*/g, '<em>$1</em>')
    }

    // Look ahead to check if a blank line is followed by another numbered item
    const isBlankBetweenOlItems = (lines, i) => {
        if (!inOl) return false
        for (let j = i + 1; j < lines.length; j++) {
            const next = lines[j].trim()
            if (next === '') continue // skip consecutive blanks
            return /^\d+[.)]\s+/.test(next) || /^\s*[-*]\s+/.test(next)
        }
        return false
    }

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trimEnd()

        // Empty line
        if (line.trim() === '') {
            // Don't close the <ol> if the next non-empty line is another
            // numbered item or a sub-item — just add spacing
            if (isBlankBetweenOlItems(lines, i)) {
                closeSubList()
                continue
            }
            closeAllLists()
            result.push('<br>')
            continue
        }

        // Headings
        const headingMatch = line.match(/^(#{1,3})\s+(.+)$/)
        if (headingMatch) {
            closeAllLists()
            const level = headingMatch[1].length
            result.push(
                `<h${level}>${formatInline(headingMatch[2])}</h${level}>`
            )
            continue
        }

        // Indented sub-item (- or * with leading whitespace) inside a numbered list
        const indentedUlMatch = line.match(/^(\s{2,})[-*]\s+(.+)$/)
        if (indentedUlMatch && inOl) {
            if (!inOlSub) {
                result.push('<ul>')
                inOlSub = true
            }
            result.push(`<li>${formatInline(indentedUlMatch[2])}</li>`)
            continue
        }

        // Top-level unordered list item
        const ulMatch = line.match(/^\s*[-*]\s+(.+)$/)
        if (ulMatch && !inOl) {
            if (!inUl) {
                result.push('<ul>')
                inUl = true
            }
            result.push(`<li>${formatInline(ulMatch[1])}</li>`)
            continue
        }

        // Numbered list item
        const olMatch = line.match(/^\s*\d+[.)]\s+(.+)$/)
        if (olMatch) {
            closeSubList()
            if (inUl) {
                result.push('</ul>')
                inUl = false
            }
            if (!inOl) {
                result.push('<ol>')
                inOl = true
            }
            result.push(`<li>${formatInline(olMatch[1])}</li>`)
            continue
        }

        // Regular paragraph
        closeAllLists()
        result.push(`<p>${formatInline(line)}</p>`)
    }

    closeAllLists()
    return result.join('')
}
